import { PerimeterEnergyTrajectoryService } from './../../../../@core/utils/perimeter-energy-trajectory.service';
import { RefrigerantTypeService } from './../../../../@core/utils/refrigerant-type.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { APIService, KPI } from '../../../../@core/api';
import { NbDialogRef, NbDialogService, NbCardModule, NbSpinnerModule, NbSelectModule, NbOptionModule, NbInputModule, NbButtonModule } from '@nebular/theme';
import { Observable, combineLatest } from 'rxjs';
import { filter, first, map, switchMap, tap, startWith } from 'rxjs/operators';
import { BasicDialogComponent } from '../../basic-dialog/basic-dialog.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ErrorService, SaveToastService } from '../../../../@core/utils';
import { DatePipe, AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-declare-refrigerant-fluid-dialog',
    templateUrl: './declare-refrigerant-fluid-dialog.component.html',
    styleUrls: ['./declare-refrigerant-fluid-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSpinnerModule,
    NbSelectModule,
    NbOptionModule,
    NbInputModule,
    NbButtonModule,
    AsyncPipe,
    TranslateModule
],
})
export class DeclareRefrigerantFluidDialogComponent implements OnInit {

  @Input() public site: KPI.Perimeter;
  @Input() public editData: any = null; 

  public loading = false;


  public operationTypes = [{
    id: 1,
    name: this.translate.instant('Reload'),
  }]

  private readonly NUMBER_VALIDATORS = [Validators.required, Validators.pattern(/^-?\d*[.,]?\d{0,2}$/)];
  private currentYear = new Date().getFullYear();
  public form = new FormGroup({
    perimeter: new FormControl(null, [Validators.required]),
    operation_type: new FormControl({value: this.operationTypes[0].id, disabled: true}, [Validators.required]),
    refrigerant_type: new FormControl(null, [Validators.required]),
    emission: new FormControl(0, this.NUMBER_VALIDATORS),
    year: new FormControl(
      this.currentYear,
      [...this.NUMBER_VALIDATORS, Validators.min(1900), Validators.max(this.currentYear)],
    ),
  });

  public perimeters: KPI.Perimeter[] = [];

  public refrigerantTypes$: Observable<KPI.RefrigerantType[]> = this.refrigerantTypeService.refrigerantTypes$;
  private siteTrajectories$: Observable<KPI.PerimeterEnergyTrajectory[]>;

  constructor(
    private dialogRef: NbDialogRef<DeclareRefrigerantFluidDialogComponent>,
    private api: APIService,
    private dialogService: NbDialogService,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef,
    private errorService: ErrorService,
    private refrigerantTypeService: RefrigerantTypeService,
    private perimeterEnergyTrajectoryService: PerimeterEnergyTrajectoryService,
    private datePipe: DatePipe,
    private saveToast: SaveToastService,
  ) { }

  public ngOnInit(): void {
    this.perimeters = this.site.sub_perimeters.filter(perimeter => !perimeter.level_parent);
    this.siteTrajectories$ = this.perimeterEnergyTrajectoryService.getRefrigerantPerimeterEnergyTrajectories(this.site.id);
    
    if (this.editData) {
      this.populateFormForEdit();
    }
    
    this.cdRef.detectChanges();
  }

  public close(): void {
    this.dialogRef.close();
  }

  private populateFormForEdit(): void {
    if (this.editData) {
      const perimeter = this.perimeters.find(p => p.name === this.editData.perimeter);
      if (perimeter) {
        this.form.patchValue({
          perimeter: perimeter.id,
          refrigerant_type: this.editData.refrigerant_type_id,
          emission: parseFloat((this.editData.emission / this.editData.gwp_20_years).toFixed(2)), 
          year: this.editData.year
        });
      }
    }
  }

  public onSubmit(): void {
    this.loading = true;
    this.cdRef.detectChanges();
    
    if (this.editData) {
      this.updateRefrigerantFluidDeclaration().subscribe(() => { }, (err) => this.errorService.handleError(err, true));
      return;
    }
    
    combineLatest([
      this.existingTrajectory$,
      this.existingTrajectoryRecordId$
    ]).pipe(
      first()
    ).subscribe(([existingTrajectory, existingConsumption]) => {
      if (!!existingConsumption) {
        const dialog = this.dialogService.open(
          BasicDialogComponent,
          {
            autoFocus: false,
            context: {
              content: this.translate.instant(
                'A declaration for {year} already exists for this perimeter Do you want to replace it ?',
                { year: this.form.get('year').value }
              ),
            }
          }
        );

        dialog.onClose.pipe(
          filter((replace) => !!replace),
          switchMap(() => this.updateRefrigerantFluidDeclaration()),
        ).subscribe(() => { }, (err) => this.errorService.handleError(err, true));

        dialog.onClose.pipe(
          filter((replace) => !replace),
        ).subscribe(() => {
          this.loading = false;
          this.cdRef.detectChanges();
        });
      } else if (!existingTrajectory) {
        this.api.add_perimeter_energy_trajectory({
          perimeter: this.form.get('perimeter').value,
          refrigerant_type: this.form.get('refrigerant_type').value,
          declaration_month: 1,
          created_on: new Date(),
        }).pipe(
          switchMap((newTrajectory) => this.addRefrigerantFluidDeclaration(newTrajectory)),
        ).subscribe(() => { }, (err) => this.errorService.handleError(err, true));
      } else {
        this.addRefrigerantFluidDeclaration(existingTrajectory).subscribe(() => { }, (err) => this.errorService.handleError(err, true));
      }
    });
  }

  private addRefrigerantFluidDeclaration(existingTrajectory: KPI.PerimeterEnergyTrajectory): Observable<{}> {
    return this.refrigerantTypes$.pipe(
      switchMap((refrigerantTypes) => this.api.add_trajectory_records({
        trajectory: existingTrajectory.id,
        year: this.form.get('year').value,
        emission: Math.round(
          parseFloat((this.form.get('emission').value * refrigerantTypes.find((elt) => elt.id === this.form.get('refrigerant_type').value).gwp_20_years).toFixed(2))
        ),
        updated_on: this.getFormattedDateFromYear(),
      })),
      first(),
      tap(() => {
        this.refrigerantTypeService.refrigerantFluidHasChanged();
        this.saveToast.showSaved();
        this.loading = false;
        this.cdRef.detectChanges();
        this.close();
      }),
    );
  }

  private updateRefrigerantFluidDeclaration(): Observable<{}> {
    if (this.editData && this.editData.trajectory_record_id) {
      return this.refrigerantTypes$.pipe(
        switchMap((refrigerantTypes) => this.api.update_trajectory_records(
          this.editData.trajectory_record_id,
          {
            year: this.form.get('year').value,
            emission: Math.round(
              parseFloat((this.form.get('emission').value * refrigerantTypes.find((elt) => elt.id === this.form.get('refrigerant_type').value).gwp_20_years).toFixed(2))
            ),
            updated_on: this.getFormattedDateFromYear(),
          }),
        ),
        first(),
        tap(() => {
          this.refrigerantTypeService.refrigerantFluidHasChanged();
          this.saveToast.showSaved();
          this.loading = false;
          this.cdRef.detectChanges();
          this.close();
        }),
      );
    }

    return combineLatest([
      this.existingTrajectoryRecordId$,
      this.refrigerantTypes$
    ]).pipe(
      switchMap(([existingTrajectoryRecordId, refrigerantTypes]) => this.api.update_trajectory_records(
        existingTrajectoryRecordId,
        {
          year: this.form.get('year').value,
          emission: Math.round(
            parseFloat((this.form.get('emission').value * refrigerantTypes.find((elt) => elt.id === this.form.get('refrigerant_type').value).gwp_20_years).toFixed(2))
          ),
          updated_on: this.getFormattedDateFromYear(),
        }),
      ),
      first(),
      tap(() => {
        this.refrigerantTypeService.refrigerantFluidHasChanged();
        this.saveToast.showSaved();
        this.loading = false;
        this.cdRef.detectChanges();
        this.close();
      }),
    );
  }

  /**
   * Find existing trajectory record id for the selected perimeter, refrigerant type and year
   */
  private get existingTrajectoryRecordId$(): Observable<number> {
    return this.siteTrajectories$.pipe(
      first(),
      map((trajectories) => {
        let extistingConsumptionId: number = null;
        trajectories.filter((trajectory) =>
          trajectory.perimeter === this.form.get('perimeter').value
          && trajectory.refrigerant_type === this.form.get('refrigerant_type').value
          && trajectory.trajectory_records.some((record) => record.year === this.form.get('year').value),
        ).forEach((trajectory) => {
          const existingRecord = trajectory.trajectory_records.find((record) => record.year === this.form.get('year').value);
          if (existingRecord) {
            extistingConsumptionId = existingRecord.id;
          }
        });
        return extistingConsumptionId;
      })
    );
  }

  /**
   * Find existing trajectory for the selected perimeter, refrigerant type
   */
  private get existingTrajectory$(): Observable<KPI.PerimeterEnergyTrajectory> {
    return combineLatest([
      this.form.get('perimeter').valueChanges.pipe(startWith(this.form.get('perimeter').value)),
      this.form.get('refrigerant_type').valueChanges.pipe(startWith(this.form.get('refrigerant_type').value)),
      this.siteTrajectories$.pipe(first()),
    ]).pipe(
      map(([selectedPerimeterId, selectedRefrigerantType, trajectories]) => trajectories.find((trajectory) =>
        trajectory.perimeter === selectedPerimeterId
        && trajectory.refrigerant_type === selectedRefrigerantType
      )),
    );
  }

  /**
   * Returns a YYYY-MM-DD formatted date from the year form control
   */
  private getFormattedDateFromYear(): string {
    return this.datePipe.transform(
      new Date(this.form.get('year').value, 0, 1),
      "yyyy-MM-dd"
    );
  }

}
